/**
 * Los apuntes del proyecto: un array JSON guardado en una sola columna de texto.
 *
 * Que sean un array dentro de una cadena obliga a dos cuidados que no hacían
 * falta cuando esto era un bloc de notas suelto, y que están aquí y no repartidos
 * por la interfaz:
 *
 *   · el tope de la columna se mide sobre el JSON serializado, nunca sobre el
 *     texto que se escribe — y NO se recorta por caracteres: cortar un array a
 *     mitad lo deja sintácticamente roto, y al volver a leerlo `parseNotes` cae
 *     en la rama de compatibilidad y convierte todas las notas en una sola con
 *     el JSON crudo dentro. Es pérdida de datos silenciosa;
 *   · escribir es reemplazar la columna entera, así que dos personas con el
 *     panel abierto se pisan salvo que lo que se manda salga de fusionar lo
 *     propio con lo que hay guardado. De eso se encarga `mergeNotes`.
 *
 * El módulo no importa nada a propósito: `lib/api` lo necesita para el tope, y
 * `lib/i18n` ya importa de `lib/api`. Traerse aquí el traductor cerraría el
 * ciclo, así que el rótulo de la nota heredada entra como parámetro.
 */
export interface ProjectNoteItem {
  id: string
  userId: string
  userName: string
  userAvatar?: string | null | undefined
  text: string
  createdAt: string
  updatedAt?: string | undefined
}

/** El tope de la columna `projects.notes`, repetido como `check` en Postgres. */
export const NOTES_MAX = 20_000

/**
 * El tope de UNA nota.
 *
 * No lo pide la base: lo pide el reparto. Sin él, una sola nota pegada desde el
 * portapapeles ocupa la columna entera y deja el widget inservible para el resto
 * del equipo, y quien la pegó no se entera hasta que falla el guardado.
 */
export const NOTE_TEXT_MAX = 2_000

/** Sin `any` de por medio: lo que no cumpla esta forma no entra. */
function isNoteLike(value: unknown): value is { id: string; text: string } & Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false
  const item = value as Record<string, unknown>
  return typeof item.id === 'string' && typeof item.text === 'string'
}

/** Lo que falte se rellena aquí, para que el resto del código no lo compruebe. */
function normalizeNote(raw: { id: string; text: string } & Record<string, unknown>): ProjectNoteItem {
  return {
    id: raw.id,
    text: raw.text,
    userId: typeof raw.userId === 'string' ? raw.userId : '',
    userName: typeof raw.userName === 'string' ? raw.userName : '',
    ...(typeof raw.userAvatar === 'string' ? { userAvatar: raw.userAvatar } : {}),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    ...(typeof raw.updatedAt === 'string' ? { updatedAt: raw.updatedAt } : {}),
  }
}

/**
 * Propaga el avatar más reciente conocido a todas las notas del mismo autor si les falta.
 *
 * Cuando alguien sube o cambia su foto de perfil, las notas antiguas guardadas
 * en el JSON de la base de datos se quedaron con `userAvatar: null` o con una
 * URL previa. Al recorrer las notas, el avatar más reciente de cada usuario se
 * comparte con el resto de sus notas para que no vuelvan a la inicial por omisión.
 */
export function propagateLatestAvatars(items: ProjectNoteItem[]): ProjectNoteItem[] {
  const latestByUserId = new Map<string, string>()
  const latestByName = new Map<string, string>()

  for (const note of items) {
    if (note.userAvatar) {
      if (note.userId && !latestByUserId.has(note.userId)) {
        latestByUserId.set(note.userId, note.userAvatar)
      }
      if (note.userName) {
        const key = note.userName.trim().toLowerCase()
        if (key && !latestByName.has(key)) {
          latestByName.set(key, note.userAvatar)
        }
      }
    }
  }

  if (latestByUserId.size === 0 && latestByName.size === 0) {
    return items
  }

  return items.map((note) => {
    if (note.userAvatar) return note
    const avatar = (note.userId ? latestByUserId.get(note.userId) : undefined)
      ?? (note.userName ? latestByName.get(note.userName.trim().toLowerCase()) : undefined)
    return avatar ? { ...note, userAvatar: avatar } : note
  })
}

export function parseNotes(raw: string, legacyAuthor = ''): ProjectNoteItem[] {
  if (!raw || !raw.trim()) return []
  const trimmed = raw.trim()
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return propagateLatestAvatars(parsed.filter(isNoteLike).map(normalizeNote))
      }
    } catch {
      // Fallback a nota legacy si el JSON es inválido
    }
  }
  return [
    {
      id: 'legacy-init',
      userId: '',
      userName: legacyAuthor,
      text: raw,
      createdAt: new Date().toISOString(),
    },
  ]
}

export function serializeNotes(items: ProjectNoteItem[]): string {
  return JSON.stringify(items)
}

/** Si esta lista cabe en la columna. Se pregunta ANTES de añadir, no después. */
export function notesFit(items: ProjectNoteItem[]): boolean {
  return serializeNotes(items).length <= NOTES_MAX
}

/** La fecha por la que se decide quién gana: la última vez que alguien la tocó. */
function stampOf(note: ProjectNoteItem): string {
  return note.updatedAt ?? note.createdAt ?? ''
}

/**
 * Lo guardado y lo mío, en una sola lista.
 *
 * Sin esto, guardar es mandar el array entero y borra lo que otro haya escrito
 * mientras tanto. Las reglas son las tres que hacen falta y ninguna más:
 *
 *   · una nota que sólo está en el servidor se conserva —la escribió otro—;
 *   · una que está en los dos gana la tocada más tarde, comparando fechas ISO,
 *     que en texto se ordenan igual que en el tiempo;
 *   · una que yo he borrado en esta sesión desaparece, aunque el servidor la
 *     siga teniendo: es la única forma de distinguir «la borré» de «no la he
 *     visto todavía».
 */
export function mergeNotes(
  server: ProjectNoteItem[],
  mine: ProjectNoteItem[],
  deleted: ReadonlySet<string> = new Set(),
): ProjectNoteItem[] {
  const byId = new Map<string, ProjectNoteItem>()
  for (const note of server) byId.set(note.id, note)
  for (const note of mine) {
    const stored = byId.get(note.id)
    if (!stored || stampOf(note) >= stampOf(stored)) byId.set(note.id, note)
  }
  for (const id of deleted) byId.delete(id)
  return propagateLatestAvatars([...byId.values()].sort((a, b) => stampOf(b).localeCompare(stampOf(a))))
}
